import { useCallback, useEffect, useRef, useState } from 'react';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { toLowerCase } from 'bl/validation/toLowerCase.bl';
import validateEmail from 'bl/validation/validateEmail.bl';
import validateName from 'bl/validation/validateName.bl';
import validatePassword from 'bl/validation/validatePassword.bl';
import { validatePhone } from 'bl/validation/validatePhone.bl';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import FormField from 'components/form/FormField.component';
import PasswordInput from 'components/form/PasswordInput.component';
import TextInput from 'components/form/TextInput.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import { FormCard } from 'components/onboarding/FormCard.component';
import { MarketingContent } from 'components/onboarding/MarketingContent.component';
import { OnboardingLayout } from 'components/onboarding/OnboardingLayout.component';
import { StepIndicator } from 'components/onboarding/StepIndicator.component';
import PhoneInput from 'components/wrapped/PhoneInput.component';
import CONF from 'config/conf.singleton';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import useAfn from 'hooks/useAfn.hook';
import useIsMobileViewport from 'hooks/useIsMobileViewport.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import selectCognitoUserNameExhaustively from 'selectors/selectCognitoUserNameExhaustively.selector';
import { COGNITO_STATE, selectCognitoConfig, selectCognitoState, selectCognitoUserPool, xeRegisterUser } from 'utils/cognito.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromSearchToQueryString } from 'utils/converters/props/path.util';
import withFeedback from 'utils/functions/withFeedback.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import batchValidate from 'utils/predicates/batchValidate.util';
import isNotFn from 'utils/predicates/function/isNotFn.util';
import { getGtmPagePath, pushToDataLayer } from 'utils/tracking';

import { trackEvent } from 'tracking/actions';

import CN from './RegisterPage.module.scss';

const CTAG = 'RegisterPage';
const L = logger(CTAG);

const MARKETING_SITE_TAS = `${CONF.marketingSite}/terms-and-conditions`;

const F = Object.freeze({
  name: 'name',
  mail: 'email',
  pass: 'password',
  phone: 'phone',
});

const MESSAGE = Object.freeze({
  [F.name]: 'common.errors.validation.noName',
  [F.mail]: 'common.errors.validation.notValidEmail',
  [F.pass]: 'common.errors.validation.passwordErrorMsg',
  [F.phone]: 'common.errors.validation.notValidPhoneNumber',
});

const VALIDATOR = Object.freeze({
  [F.name]: validateName,
  [F.mail]: validateEmail,
  [F.pass]: validatePassword,
  [F.phone]: validatePhone,
});

const FormCardFooter = () => {
  const { t } = useTranslation();

  return (
    <>
      <p className={CN.cardNote}>{t('register.formCardNote')}</p>
      <div data-bem="RegisterPage__disclaimer" className={CN.disclaimer}>
        <span className={CN.disclaimerText}>{t('register.agreementMsg')}</span>
        <a href={MARKETING_SITE_TAS} className={CN.toc}>
          {t('register.termsAndConditions')}
        </a>
      </div>
    </>
  );
};

export const RegisterPage = () => {
  const history = useHistory();
  const location = useLocation();
  const searchQuery = fromSearchToQueryString(location.search);
  const nav = navFor(CTAG, { history });
  const isMobileViewport = useIsMobileViewport();

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const cognitoUsername = useSelector(selectCognitoUserNameExhaustively);
  const cognitoState = useSelector(selectCognitoState);
  const userPool = useSelector(selectCognitoUserPool);
  const config = useSelector(selectCognitoConfig);

  const isRegistered = cognitoState === COGNITO_STATE.in || cognitoState === COGNITO_STATE.needConfirmation;
  const isChecking = cognitoState === COGNITO_STATE.loading;

  const [fields, setFields] = useStateWhileMounted({ [F.name]: S.empty, [F.phone]: S.empty, [F.mail]: S.empty });
  const [cognitoError, setCognitoError] = useState();
  const { val: isLoading, set: setIsLoading } = useTip(false);
  const didTrackSignupFormStartRef = useRef(false);

  const isNextEnabled = batchValidate({ validators: VALIDATOR, fields });

  const name = fields[F.name];
  const email = fields[F.mail];
  const password = fields[F.pass];
  const phoneNumber = fields[F.phone];

  const handleDismiss = useCallback(() => setCognitoError(''), []);

  const handleLogin = useCallback(() => {
    setCognitoError('');
    nav.push({ pathname: LINK.login });
  }, [nav]);

  const handleTrackEvent = useCallback((event) => dispatch(trackEvent(event)), [dispatch]);
  const handleRegisterUser = useCallback((options) => dispatch(xeRegisterUser(options)), [dispatch]);

  const handleChange = useCallback(
    (ev) => {
      const { name: fieldName, raw } = asObject(ev);
      const value = fieldName === F.mail ? toLowerCase(raw) : raw;
      if (fields[fieldName] === value) return;

      setFields({ ...fields, [fieldName]: value });
    },
    [fields, setFields],
  );

  const handlePhoneChange = useCallback(
    (ev) => {
      const { name: fieldName, ev: value } = asObject(ev);
      if (fields[fieldName] === value) return;

      setFields({ ...fields, [fieldName]: value });
    },
    [fields, setFields],
  );

  const handleValidate = useCallback(
    (ev) => {
      const { name: fieldName, dirty, raw: value } = asObject(ev);
      const validate = VALIDATOR[fieldName];

      // no point validating if there wasn't even a single change
      if (!dirty) return null;
      if (isNotFn(validate)) return null;

      // return null to clear the error from before
      return { error: validate({ value }) ? null : t(MESSAGE[fieldName]) };
    },
    [t],
  );

  const validatePhone = useCallback(
    (ev) => {
      const { name: fieldName, ev: value } = asObject(ev);
      const validate = VALIDATOR[fieldName];

      if (isNotFn(validate)) return null;

      // return null to clear the error from before
      return { error: validate({ value }) ? null : t(MESSAGE[fieldName]) };
    },
    [t],
  );

  const [, handleClick] = useAfn(async () => {
    if (!isNextEnabled) return;

    try {
      const pn = isPossiblePhoneNumber(phoneNumber);
      const attributes = pn ? { name, email, phone_number: phoneNumber } : { name, email };
      const options = { userPool, config, username: email, password, attributes };

      const register = withFeedback((value) => void setIsLoading(value || isChecking), handleRegisterUser);
      const result = await register(options);
      L.debug('handleClick()', 'result:', result);

      // No SEGMENT.identify here - registerUser auto-signs the user in, which dispatches
      // COGNITO_LOGIN, which fires trackCognitoLogin with the proper sub + full traits.
      // Identifying with email here previously created a second Intercom contact during
      // signup because trackCognitoLogin then identified again with a different id.

      handleTrackEvent('NEW_USER_CREATED');
      pushToDataLayer('signup_step1_submit', { step_number: 1, page_path: getGtmPagePath() });
      nav.push({ pathname: LINK.onboardingInfo, search: searchQuery });
    } catch (error) {
      L.warn('handleClick()', 'phoneNumber:', phoneNumber, error);
      setCognitoError(error);
    }
  }, [isNextEnabled, phoneNumber, name, email, userPool, config, password, handleRegisterUser, handleTrackEvent, nav, searchQuery, setIsLoading, isChecking]);

  useEffect(() => {
    if (!isRegistered) return;

    // a registered user doesn't need this form, so go to the next one
    L.info('useEffect()', { cognitoState, cognitoUsername }, 'moving on to the next step...');
    nav.push({ pathname: LINK.onboardingInfo, search: searchQuery });
  }, [cognitoUsername, cognitoState, nav, searchQuery, isRegistered]);

  useEffect(() => {
    if (isRegistered || didTrackSignupFormStartRef.current) return;
    didTrackSignupFormStartRef.current = true;
    pushToDataLayer('signup_form_start', { page_path: getGtmPagePath() });
  }, [isRegistered]); // eslint-disable-line react-hooks/exhaustive-deps -- pushToDataLayer / getGtmPagePath are stable imports

  L.debug('()', fields);

  if (isRegistered) {
    // if user is logged in, the creation step is finished, just return null
    L.debug('()', 'User already isRegistered:', isRegistered, 'returning', null);
    return null;
  }

  const isErrorModalOpen = fromAnyToBool(cognitoError);

  return (
    <div data-bem="RegisterPage" className={CN.component}>
      <SalonModal tag="RegisterPage__cognitoError" isOpen={isErrorModalOpen}>
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button variant="empty" onClick={handleDismiss}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>

        <SalonModal.Body>
          <IconStatus variant="alert" width="md">
            <Icon variant="alert" width="l1" />
          </IconStatus>
          <div className={CN.modalContent}>
            <div className={CN.modalTitle}>{cognitoError}</div>
            <div className={CN.modalText}>{t('register.errorModalText')}</div>
          </div>
        </SalonModal.Body>

        <SalonModal.Footer>
          <Button variant="outlineForce" casing="dialog" onClick={handleLogin}>
            {t('register.login')}
          </Button>
        </SalonModal.Footer>
      </SalonModal>

      <OnboardingLayout mobileHeader={<MarketingContent.Header />}>
        <FormCard submitButtonLabel={t('register.getStartedButton')} isSubmitDisabled={!isNextEnabled} isLoading={isLoading} onSubmit={handleClick} footer={<FormCardFooter />}>
          <StepIndicator currentStep={1} />

          <div data-bem="RegisterPage__name-row" className={CN.formField}>
            <FormField
              input={TextInput}
              name={F.name}
              label={t('register.fullNameLabel')}
              placeholder={t('register.fullNamePlaceholder')}
              value={name}
              autoFocus
              onChange={handleChange}
              onValidate={handleValidate}
            />
          </div>
          <div data-bem="RegisterPage__email-row" className={CN.formField}>
            <FormField
              input={TextInput}
              name={F.mail}
              label={t('register.emailLabel')}
              placeholder={t('register.emailPlaceholder')}
              value={email}
              onChange={handleChange}
              onValidate={handleValidate}
            />
          </div>
          <div data-bem="RegisterPage__phone-row" className={CN.formField}>
            <FormField
              input={PhoneInput}
              inputMode="decimal"
              name={F.phone}
              label={t('register.phoneNumberLabel')}
              placeholder={t('register.phoneNumberPlaceholder')}
              value={phoneNumber}
              onChange={handlePhoneChange}
              onValidate={validatePhone}
              className={CN.phoneCountrySelect}
            />
          </div>
          <div data-bem="RegisterPage__password-row" className={CN.formField}>
            <FormField
              input={PasswordInput}
              type="password"
              autoComplete="new-password"
              name={F.pass}
              label={t('register.passwordLabel')}
              placeholder={t('register.passwordPlaceholder')}
              value={password}
              onChange={handleChange}
              onValidate={handleValidate}
            />
          </div>
        </FormCard>

        {isMobileViewport && (
          <div className={CN.footerBullets}>
            <MarketingContent.Bullets />
          </div>
        )}
      </OnboardingLayout>

      {isLoading && !cognitoError && <div data-bem="RegisterPageV2__loading" className={CN.loadBlocker} />}
    </div>
  );
};
